import { createSelector } from 'reselect'

import {
	AssignCoachDataTableModel,
	Coach,
	CoachesHistiryLogTableModel,
	HistoryLogCoachData,
} from '@app/types/coaches'
import {
	selectOrganizationList,
	selectUsersTimeLocale,
} from '../user/selectors'
import {
	selectCountries,
	selectExpertises,
	selectSkillsList,
	selectSystemConfigCarbs,
	selectSystemConfigLanguages,
} from '../systemConfig/selectors'
import {
	GLUCOSE_ITEMS,
	UNITS_ITEMS,
	DATE_FORMAT_ITEMS,
	TEMPERATURE_ITEMS,
} from '@app/constants/localization'
import { RootState } from '@app/store'
import { Organization } from '@app/types/user'
import { createSearchRegex, getAnyTitle } from '@app/helpers'
import { selectTimeFormatter } from '@app/hooks/contexts/timeController/formatters'

import { selectLocales } from '../locale/selectors'

export const selectCoaches = (state: RootState): Coach[] =>
	state.appReducer.searchCoaches?.coachesDataArray || []

export const selectCoachesCount = (state: RootState): any =>
	state.appReducer.searchCoaches?.coachesDataArray.length || 0

export const selectSelectedAssignCoach = (state: RootState): string[] =>
	state.appReducer.searchCoaches?.selectedAssignCoach || []

export const selectSelectedCoachName = (state: RootState): string =>
	state.appReducer.searchCoaches?.selectedCoach?.personal?.name || ''

export const selectSelectedCoach = (state: RootState): any =>
	state.appReducer.searchCoaches?.selectedCoach || {}

export const selectSelectedCoachOrganizations = (
	state: RootState
): Organization[] =>
	state.appReducer.searchCoaches?.selectedCoach?.organizations || []

export const selectSelectedCoachSkills = (state: RootState): string[] =>
	state.appReducer.searchCoaches?.selectedCoach?.employment?.skills || []

export const selectSelectedCoachId = (state: RootState): string =>
	state.appReducer.searchCoaches?.selectedCoach?._id || ''

export const selectCoachHistoryLog = (
	state: RootState
): HistoryLogCoachData[] =>
	state.appReducer.searchCoaches?.historyLogCoach || []

export const selectCoachHistorySearch = (state: RootState): string =>
	state.appReducer.searchCoaches?.coachHistorySearch || ''

export const selectCoachHistoryLogData = createSelector(
	[selectCoachHistoryLog, selectCoachHistorySearch, selectUsersTimeLocale],
	(historyLogs, serchHistory, timeLocale) => {
		const dF = selectTimeFormatter(timeLocale, 'df')

		return historyLogs
			.filter(story => {
				if (!serchHistory) return true
				return (
					dF(story?.timestamp).includes(serchHistory) ||
					story.activity.includes(serchHistory) ||
					story.patientData?.name.includes(serchHistory) ||
					story.accessedScreen.includes(serchHistory)
				)
			})
			.map((h: HistoryLogCoachData): CoachesHistiryLogTableModel => {
				return {
					_id: h?._id,
					action: h?.activity,
					date: dF(h?.timestamp),
					patient: h?.patientData?.name,
					notes: h?.accessedScreen,
				}
			})
	}
)

export const selectCoachEmployment = createSelector(
	[
		selectSelectedCoach,
		selectLocales,
		selectSkillsList,
		selectExpertises,
		selectOrganizationList,
		selectUsersTimeLocale,
	],
	(
		coach,
		locales,
		skillsList,
		expertiseList,
		allOrganizations,
		timeLocale
	) => {
		const dF = selectTimeFormatter(timeLocale, 'df')

		const startOfService = coach?.employment?.startOfService

		const organization = (): string => {
			return (
				allOrganizations.find(
					o => o._id === coach?.extra?.organizationID
				)?.orgName || ''
			)
		}

		const dateStarted = startOfService ? dF(startOfService) : 'N/A'

		const role =
			coach.employment?.roles
				?.map((role: any) => getAnyTitle(role))
				.join(', ') || 'N/A'

		const roleTypes = (): string => {
			if (Array.isArray(coach.employment?.roleTypes))
				return coach.employment?.roleTypes
					?.map((roleTypes: any) => getAnyTitle(roleTypes))
					.join(', ')
			return getAnyTitle(coach.employment?.roleTypes) || 'N/A'
		}
		const getSkills = (): string => {
			const result = coach?.employment?.skills?.map((skill: any) => {
				if (!skill) return 'N/A'
				const skillName = skillsList.find(
					item => +item.id === +skill
				)?.ui_name
				if (!skillName) return 'N/A'
				return locales[skillName]
			})
			return result.join(', ') || 'N/A'
		}
		const getExspetise = (): any => {
			const result = coach?.employment?.expertise?.map((exp: any) => {
				if (!exp) return 'N/A'
				const expertiseName = expertiseList.find(
					item => +item.id === +exp
				)?.ui_name
				if (!expertiseName) return 'N/A'
				return locales[expertiseName]
			})
			return result.join(', ') || 'N/A'
		}
		return {
			organization: organization(),
			dateStarted,
			role,
			roleTypes: roleTypes(),
			skills: getSkills(),
			expertise: getExspetise(),
			jobScope: coach?.employment?.positionScope || 'N/A',
			patientsCapacityLoad:
				coach?.employment?.patientCapacityLoad || 'N/A',
			notes: coach?.personal?.notes || 'N/A',
		}
	}
)
export const selectCoachLocalization = createSelector(
	[
		selectSelectedCoach,
		selectLocales,
		selectSystemConfigCarbs,
		selectSystemConfigLanguages,
	],
	(coach, locales, carbs, langs) => {
		const getCarbs = (): string => {
			if (!coach?.localization?.carbs) return 'N/A'
			// eslint-disable-next-line max-len
			const carbsTitle = carbs.find(
				(carbs: any): any => carbs.id === coach?.localization?.carbs
			)?.ui_text
			if (!carbsTitle) return 'N/A'
			return locales[carbsTitle] || `${carbsTitle}`
		}
		const getLang = (): string => {
			if (!coach?.localization?.language) return 'N/A'
			// eslint-disable-next-line max-len
			const langsTitle = langs.find(
				(carbs: any): any =>
					carbs.name === coach?.localization?.language
			)?.ui_text
			if (!langsTitle) return 'N/A'
			return getAnyTitle(langsTitle)
		}
		interface LocalItems {
			value: string
			title: string
		}
		const getLocalTitle = (array: LocalItems[], value: string): string => {
			if (coach?.localization?.glucoseLevel) {
				const gluoseTitle = array.find(
					(item: LocalItems) => item.value === value
				)?.title
				return gluoseTitle || coach?.localization?.glucoseLevel
			}
			return 'N/A'
		}

		return {
			language: getLang(),
			glucoseLevel: getLocalTitle(
				GLUCOSE_ITEMS,
				coach?.localization?.glucoseLevel
			),
			weightHeight: getLocalTitle(
				UNITS_ITEMS,
				coach?.localization?.weightHeight
			),
			dateConfiguration: getLocalTitle(
				DATE_FORMAT_ITEMS,
				coach?.localization?.dateConfiguration
			),
			carbs: getCarbs(),
			temperature: getLocalTitle(
				TEMPERATURE_ITEMS,
				coach?.localization?.temperature
			),
		}
	}
)

export const selectAssignCoachSearchValue = (state: RootState): string =>
	state.appReducer?.searchCoaches?.coachAssignSearch || ''

export const selectAssignCoaches = createSelector(
	[selectCoaches, selectAssignCoachSearchValue, selectCountries],
	(coaches, searchValue, countries) => {
		const assignTableCoaches: AssignCoachDataTableModel[] = coaches.map(
			coach => {
				const country = countries.find(
					country => country.id === coach.personal?.Country
				)

				const capacityLoad = coach.numOfPatients
					? Math.round(
							((coach.numOfPatients || 0) /
								coach.employment.patientCapacityLoad) *
								100
					  )
					: 0

				return {
					id: coach._id,
					name: coach.personal?.name
						? coach.personal?.name
						: `${coach.personal?.firstName || ''} ${
								coach.personal?.lastName || ''
						  }`,

					role: coach.employment?.roles?.join(',') || '',
					numOfPatients: coach.numOfPatients || 0,
					language: coach.localization.language || '',
					imageUrl: coach.personal?.imageUrl || '',
					capacity: coach.employment.patientCapacityLoad,
					capacityLoad,
					state: coach.personal.state || '',
					country: country?.name || '',
				}
			}
		)

		if (!searchValue) return assignTableCoaches

		const searchingRegex = createSearchRegex(searchValue)

		return assignTableCoaches.filter(coach => {
			return !!coach.name?.toLowerCase()?.match(searchingRegex)
		})
	}
)

export const selectCoachSelectedId = (state: RootState): string =>
	state.appReducer?.searchCoaches?.selectedCoachId || ''

export const selectAssignCoachesLoadingStatus = (state: RootState): boolean =>
	state.appReducer?.searchCoaches?.assignCoachesLoading || false
